import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { finalize, take } from 'rxjs/operators';

import { ReaImportService } from '../../../../service/fincas/rea-import.service';

import { StorageManager } from 'src/app/common/storage-manager.class';
import { environment } from 'src/environments/environment';
import { BaseResponseModel } from 'src/app/models/base-response.model';
import { 
  FincaReaRequest, 
  HistoricoREAModel, 
  ReaImport422Data, 
  ReaImportData, 
  ReaImportSummary, 
  ReaMessageState,
  ReaModalModel
} from 'src/app/models/fincas/rea.model';


@Component({
  selector: 'app-rea-import-modal',
  templateUrl: './rea-import-modal.component.html',
  styleUrls: ['./rea-import-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReaImportModalComponent {
  @Input({ required: true })
  public visible: boolean = false;

  @Output()
  public readonly visibleChange = new EventEmitter<boolean>();

  @Input({ required: true })
  public model!: ReaModalModel;

  /** If parent provides it, we show it; otherwise we fetch on dialog open. */
  @Input()
  public historicoREA: BaseResponseModel<HistoricoREAModel> | null = null;

  @Output()
  public readonly imported = new EventEmitter<BaseResponseModel<ReaImportData>>();

  public showSpinner: boolean = false;
  public messageState: ReaMessageState = '';
  public importResult: BaseResponseModel<ReaImportData> | null = null;

  /** Avoid re-fetching history if dialog opens multiple times with same REA */
  private lastHistoryCodigoRea: string = '';

  /** Dialog breakpoints (no magic numbers in template) */
  public readonly REA_DIALOG_BREAKPOINTS: Readonly<Record<string, string>> = {
    '1600px': '48vw',
    '1199px': '65vw',
    '768px': '85vw',
    '480px': '95vw',
  };

  constructor(
    private readonly reaImportService: ReaImportService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  /** Hide "Enviar" after receiving any server response */
  public get canSend(): boolean {
    return this.importResult == null;
  }

  /** Disabled state for REA button (no string/length literals in template) */
  public get isReaDisabled(): boolean {
    const code = this.model?.codigo_REA;
    return (code == null) || String(code).trim().length === 0;
  }

  /** Header helper for templates */
  public get lastReaFecha(): string {
    return this.historicoREA?.firstOrNull()?.fecha ?? '';
  }

  public hide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetState();
    this.cdr.markForCheck();
  }

  /** Triggered by (onShow) in the dialog. */
  public getLastRegisterREA(): void {
    const codigoREA = String(this.model?.codigo_REA ?? '').trim();
    if (codigoREA.length === 0) { return; }

    // Don’t spam same request if reopening dialog for same finca
    if (codigoREA === this.lastHistoryCodigoRea && this.historicoREA != null) { return; }
    this.lastHistoryCodigoRea = codigoREA;

    this.reaImportService.getLastRegisterRea(codigoREA).pipe(take(1)).subscribe((res) => {
      // Service already returns BaseResponseModel, but keep it bulletproof if backend misbehaves
      this.historicoREA = BaseResponseModel.normalize<HistoricoREAModel>(res);
      this.cdr.markForCheck();
    });
  }

  public exportREA(): void {
    if (this.showSpinner) { return; } // prevent double click while loading

    const payload = this.buildPayload(this.model);
    if (!payload) {
      this.messageState = 'warning';
      this.cdr.markForCheck();
      return;
    }

    this.showSpinner = true;
    this.messageState = '';
    this.importResult = null;
    this.cdr.markForCheck();

    this.reaImportService.importRea(payload).pipe(
      take(1),
      finalize(() => {
        this.showSpinner = false;
        this.cdr.markForCheck();
      }),
    ).subscribe((resp) => {
      // Service returns BaseResponseModel already (normalized + error-mapped)
      const improved = this.withBetter422Message(resp);

      this.importResult = improved;
      this.messageState = improved.isOk ? 'success' : 'error';

      this.imported.emit(improved);
      this.cdr.markForCheck();
    });
  }

  private resetState(): void {
    this.showSpinner = false;
    this.messageState = '';
    this.importResult = null;
  }

  private buildPayload(data: ReaModalModel): FincaReaRequest | null {
    const idCliente = data.id_cliente;
    const codigoRea = String(data.codigo_REA ?? '').trim();
    const nif = String(data.titular_nif ?? '').trim();
    const nombreFinca = String(data.nombre_finca ?? '').trim();

    if ((environment.features.hasClients && !idCliente) || !codigoRea || !nif || !nombreFinca) {
      return null;
    }

    const user = StorageManager.getUser();

    return {
      operationType: 'import',
      autonomousCommunity: user.codigo_ine ?? environment.features.codigoINE,
      fincaName: nombreFinca,
      rea: codigoRea,
      nif,
      userId: Number(user.id),
      clientId: idCliente ?? 0,
    };
  }

  private withBetter422Message(resp: BaseResponseModel<ReaImportData>): BaseResponseModel<ReaImportData> {
    if (resp.statusCode !== 422) { return resp; }

    const firstItem = resp.firstOrNull() as ReaImport422Data | null;
    const firstMsg = firstItem?.errores?.[0]?.descripcion?.trim();

    return firstMsg ? resp.with({ statusMessage: firstMsg }) : resp;
  }

  public get importSummary(): ReaImportSummary | null {
    if (this.importResult?.statusCode !== 202) { return null; }

    const first = this.importResult.firstOrNull() as unknown;

    return (first && typeof first === 'object' && 'parcelasInsertadas' in first)
      ? (first as ReaImportSummary)
      : null;
  }
}
